import {
  Auth,
  EmailAuthProvider,
  getAuth,
  linkWithCredential,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  User,
} from "firebase/auth";
import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

import FuturHealthLegacyAPI from "../utils/FuturHealthLegacyAPI";
import { Payload } from "../typings";
import localforage from "localforage";
import { clearPersistedStore } from "../store/persistStore";
import {
  setupPushNotifications,
  getDeliveredNotifications,
} from "../services/notificationService";
import { Capacitor } from "@capacitor/core";
import { datadogAddAction } from "../utils/datadogHelper";
import { retryOnNetworkFail } from "../utils/retryOnNetworkFail";
import { assertVerifiedPasswordCredential } from "../utils/firebaseAuthCredentials";
import { getUserProfile } from "../services/firestoreService";
import {
  AppEntrypointValidationResult,
  validateAppEntrypoint,
} from "../utils/appEntrypoint";

export interface WrongAppSession {
  validation: AppEntrypointValidationResult & { isValid: false };
  userId: string;
  appEntrypoint: string | undefined;
}

export interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  legacyApiUserToken: string | null;
  wrongAppSession: WrongAppSession | null;
  clearWrongAppSession: () => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutAsCanceled: () => Promise<void>;
  getSubscriptionCanceled: () => boolean;
  anonymousLogin: () => Promise<User | null>;
  upgradeAnonUserWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<void>;
  updateAuthProfile: (displayName: string) => Promise<void>;
  getLegacyApiUserToken: (user: User) => Promise<string | null>;
  logInWithCustomToken: (customToken: string) => Promise<User>;
  updateUserProfile: (profile: Partial<User>) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isLoadingTokenRef = useRef(false);
  const [subscriptionCanceled, setSubscriptionCanceled] =
    useState<boolean>(false);
  const [legacyApiUserToken, setLegacyApiUserToken] = useState<string | null>(
    null
  );
  const legacyApiUserTokenRef = useRef<string | null>(null);
  const [wrongAppSession, setWrongAppSession] =
    useState<WrongAppSession | null>(null);
  const isInitialAuthCheckRef = useRef(true);
  const auth: Auth = getAuth();

  const getLegacyApiUserToken = useCallback(
    async (user: User) => {
      if (user && user.email && !isLoadingTokenRef.current) {
        // Check both passed user and live auth.currentUser for displayName
        // (updateUserProfile sets displayName on auth.currentUser, but React state might be stale)
        const displayName = user.displayName || auth.currentUser?.displayName;
        if (!displayName) {
          // Clear stale legacy token when no display name is available.
          // Without this, a previous user's token could persist in context if they had a displayName
          // but the new user doesn't (e.g., during account switching or incomplete registration).
          // This prevents security leaks where User B could access User A's legacy API token.
          setLegacyApiUserToken(null);
          return null;
        }

        try {
          isLoadingTokenRef.current = true;
          const parts = displayName.split(" ");
          const firstName = parts.shift();
          const lastName = parts.join(" ");
          const firebaseToken = await user.getIdToken();
          const payload: Partial<Payload> = {
            email: user.email,
            first_name: firstName,
            last_name: lastName,
            firebase_auth_id: user.uid,
          };
          const { data } = await FuturHealthLegacyAPI.getUserToken(
            payload,
            firebaseToken
          );
          setLegacyApiUserToken(data.token);
          return data.token;
        } catch (error) {
          console.error("error fetching legacy user:", error);
          setLegacyApiUserToken(null);
          return null;
        } finally {
          isLoadingTokenRef.current = false;
        }
      }
      return null;
    },
    [auth]
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const userCredential = await retryOnNetworkFail(() =>
        signInWithEmailAndPassword(auth, email, password)
      );
      setSubscriptionCanceled(false);
      if (userCredential.user) {
        await getLegacyApiUserToken(userCredential.user as User);
      }
    },
    [auth, getLegacyApiUserToken]
  );

  const logInWithCustomToken = useCallback(
    async (customToken: string) => {
      datadogAddAction("AuthContext: logInWithCustomToken called", {
        currentUserBefore: currentUser?.uid,
        timestamp: new Date().toISOString(),
      });
      const userCredential = await retryOnNetworkFail(() =>
        signInWithCustomToken(auth, customToken)
      );
      datadogAddAction("AuthContext: signInWithCustomToken succeeded", {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        isAnonymous: userCredential.user.isAnonymous,
        timestamp: new Date().toISOString(),
      });
      setSubscriptionCanceled(false);
      if (userCredential.user) {
        setCurrentUser(userCredential.user as User);
        datadogAddAction("AuthContext: setCurrentUser called with new user", {
          uid: userCredential.user.uid,
          timestamp: new Date().toISOString(),
        });
        await getLegacyApiUserToken(userCredential.user as User);
      }
      // Returned because `currentUser` is state: a caller that awaits this
      // still holds the pre-sign-in value until React re-renders.
      return userCredential.user as User;
    },
    [auth, getLegacyApiUserToken, currentUser]
  );

  const updateUserProfile = useCallback(
    async (profile: Partial<User>) => {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("No authenticated user");
      }
      await updateProfile(user, profile);
    },
    [auth]
  );

  // REMOVED: Duplicate Braze user identification logic
  // This was causing multiple Braze profiles to be created
  // Braze user identification is now handled by useBraze hook with proper sequencing

  const logout = useCallback(async () => {
    await signOut(auth);
    setCurrentUser(null);
    setLegacyApiUserToken(null);
    localforage.clear().catch(() => {});
    clearPersistedStore();
  }, [auth]);

  const logoutAsCanceled = async () => {
    setSubscriptionCanceled(true);
    await signOut(auth);
  };

  const getSubscriptionCanceled = () => {
    return subscriptionCanceled;
  };

  const anonymousLogin = useCallback(async () => {
    const result = await retryOnNetworkFail(() => signInAnonymously(auth));
    return result.user;
  }, [auth]);

  const upgradeAnonUserWithEmailAndPassword = async (
    email: string,
    password: string
  ) => {
    try {
      const authenticatedUser = auth.currentUser;
      if (!authenticatedUser) {
        throw new Error("No authenticated Firebase user to link");
      }

      const expectedUid = authenticatedUser.uid;
      const credential = EmailAuthProvider.credential(email, password);
      const result = await linkWithCredential(authenticatedUser, credential);

      // linkWithCredential returns the real Firebase Web SDK user used by the
      // rest of this context and Firestore. Keep context synchronized even if
      // the post-link provider/email assertion fails, so retries do not attempt
      // to link an already-linked credential against stale state.
      if (result.user.uid !== expectedUid) {
        assertVerifiedPasswordCredential(result.user, email, expectedUid);
      }
      try {
        await result.user.reload();
      } catch (reloadError) {
        // Linking may already be committed even when the follow-up reload fails.
        // Only accept that partial success when the link response itself proves
        // the expected UID, email, and password provider were preserved.
        assertVerifiedPasswordCredential(result.user, email, expectedUid);
        datadogAddAction(
          "AuthContext: reload failed after verified credential link",
          {
            userId: expectedUid,
            error:
              reloadError instanceof Error
                ? reloadError.message
                : String(reloadError),
            timestamp: new Date().toISOString(),
          }
        );
      } finally {
        setCurrentUser(result.user);
      }

      assertVerifiedPasswordCredential(result.user, email, expectedUid);
    } catch (err) {
      console.error("error upgrading user: ", err);
      throw err;
    }
  };

  const updateAuthProfile = async (displayName: string) =>
    updateProfile(currentUser as User, { displayName }).catch((err) =>
      console.error("error upgrading user: ", err)
    );

  useEffect(() => {
    if (!currentUser && Capacitor.getPlatform() !== "web") {
      getDeliveredNotifications();
    }
  }, [currentUser]);

  useEffect(() => {
    legacyApiUserTokenRef.current = legacyApiUserToken;
  }, [legacyApiUserToken]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // On the first callback with a non-null user the session was restored from
      // local persistence — the user never re-submitted the login form, so the
      // FLEX-186 app_entrypoint gate in handleSubmit never ran. Check here and
      // sign out before setting currentUser so isAuthenticated stays false.
      const isSessionRestore = isInitialAuthCheckRef.current && !!user;
      isInitialAuthCheckRef.current = false;

      if (isSessionRestore) {
        let blocked = false;
        // Unlike the login form (where Firebase Auth is a separate gate),
        // session restore has no other gate — fail-open here means a migrated
        // user gets a fully authenticated session. Retry twice before giving up.
        const RETRY_DELAYS_MS = [300, 600];
        let lastErr: unknown;
        let profileFetched = false;
        for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
          try {
            const profile = await getUserProfile(user);
            profileFetched = true;
            const validation = validateAppEntrypoint(profile?.app_entrypoint);
            if (!validation.isValid) {
              blocked = true;
              if (auth.currentUser?.uid !== user.uid) break;
              await signOut(auth);
              setWrongAppSession({
                validation,
                userId: user.uid,
                appEntrypoint: profile?.app_entrypoint,
              });
            }
            break;
          } catch (err) {
            lastErr = err;
            const code = (err as { code?: string })?.code;
            const isTransient =
              !code || code === "unavailable" || code === "deadline-exceeded";
            if (isTransient && attempt < RETRY_DELAYS_MS.length) {
              await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
            } else {
              break;
            }
          }
        }
        if (!profileFetched) {
          datadogAddAction(
            "app_entrypoint session-restore check failed; failing open",
            {
              userId: user.uid,
              error:
                lastErr instanceof Error ? lastErr.message : String(lastErr),
            }
          );
          console.error(
            "app_entrypoint session-restore check failed after retries; allowing in",
            lastErr
          );
        }
        // Guard against a newer auth event that completed while we were awaiting
        // getUserProfile or signOut — bail out and let that callback own state.
        if (auth.currentUser?.uid !== user.uid) return;

        if (blocked) {
          setCurrentUser(null);
          setLoading(false);
          return;
        }
      }

      await setupPushNotifications(user?.uid);
      setCurrentUser(user as User);
      setLoading(false);
      if (!legacyApiUserTokenRef.current && user) {
        await getLegacyApiUserToken(user);
      }
    });

    return () => unsubscribe();
  }, [auth, getLegacyApiUserToken]);

  const clearWrongAppSession = useCallback(() => setWrongAppSession(null), []);

  const value: AuthContextType = {
    currentUser,
    isAuthenticated: currentUser !== null && !currentUser.isAnonymous,
    legacyApiUserToken,
    wrongAppSession,
    clearWrongAppSession,
    login,
    logout,
    logoutAsCanceled,
    getSubscriptionCanceled,
    anonymousLogin,
    upgradeAnonUserWithEmailAndPassword,
    updateAuthProfile,
    getLegacyApiUserToken,
    logInWithCustomToken,
    updateUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <div data-testid="loading-indicator" /> : children}
    </AuthContext.Provider>
  );
};
